import moment from "moment";
import { head, filter, prop, propEq, isNil, sum, map } from "ramda";

/**
 * Servicio centralizado para cálculos de tours
 * Proporciona métodos estandarizados para todos los cálculos relacionados con tours
 * y asegura consistencia entre diferentes componentes
 */
class TourCalculationService {
  /**
   * Calcula los días efectivos entre dos fechas de manera consistente
   * @param {string} startDate - Fecha de inicio
   * @param {string} endDate - Fecha de fin
   * @returns {number} Número de días efectivos
   */
  static calculateEffectiveDays(startDate, endDate) {
    if (!startDate || !endDate) {
      console.warn('TourCalculationService: Fechas inválidas para calcular días efectivos');
      return 0;
    }

    const start = moment(startDate).utc();
    const end = moment(endDate).utc();
    const days = end.diff(start, "days");
    return days + 1; // Incluir el día de inicio
  }

  /**
   * Calcula el precio base del tour según el modelo de vehículo
   * @param {Object} tourData - Datos del tour
   * @param {string} modelName - Nombre del modelo
   * @param {Array} tourPrices - Lista de precios del tour
   * @returns {number} Precio base
   */
  static calculateBasePrice(tourData, modelName, tourPrices) {
    if (!tourPrices || !Array.isArray(tourPrices)) {
      console.warn('TourCalculationService: tourPrices inválido');
      return 0;
    }

    if (modelName === "Own Motorcycle") {
      const passengerPrice = this.getPriceBySubType(tourPrices, "tour_passenger");
      return passengerPrice + 500; // Cargo adicional para moto propia
    }
    
    const tourTotal = this.getPriceBySubType(tourPrices, "tour_total");
    return tourTotal;
  }

  /**
   * Calcula el precio de GPS con días efectivos corregidos
   * @param {boolean} hasGps - Si tiene GPS habilitado
   * @param {Array} gpsPrices - Lista de precios de GPS
   * @param {number} effectiveDays - Días efectivos
   * @param {string} modelName - Nombre del modelo
   * @returns {number} Precio de GPS
   */
  static calculateGpsPrice(hasGps, gpsPrices, effectiveDays, modelName) {
    if (!hasGps || modelName === "K1600GT") {
      return 0;
    }

    if (!gpsPrices || !Array.isArray(gpsPrices)) {
      console.warn('TourCalculationService: gpsPrices inválido');
      return 0;
    }

    const gpsPrice = this.getPriceByType(gpsPrices, "gps_device");
    return gpsPrice * effectiveDays;
  }

  /**
   * Calcula el precio del seguro VIP
   * @param {boolean} hasInsurance - Si tiene seguro habilitado
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {number} effectiveDays - Días efectivos
   * @param {string} modelName - Nombre del modelo
   * @returns {number} Precio del seguro VIP
   */
  static calculateVipInsurancePrice(hasInsurance, insurancePrices, effectiveDays, modelName) {
    if (!hasInsurance || modelName === "Own Motorcycle") {
      return 0;
    }

    if (!insurancePrices || !Array.isArray(insurancePrices)) {
      console.warn('TourCalculationService: insurancePrices inválido');
      return 0;
    }

    const insurancePrice = this.getPriceById(insurancePrices, 1); // ID del seguro VIP
    return insurancePrice * effectiveDays;
  }

  /**
   * Calcula el precio del pasajero
   * @param {Object} tourData - Datos del tour
   * @param {Array} tourPrices - Lista de precios del tour
   * @returns {number} Precio del pasajero
   */
  static calculatePassengerPrice(tourData, tourPrices) {
    if (!tourData || !tourData.booking || !tourData.booking.p_first_name) {
      return 0;
    }

    return this.getPriceBySubType(tourPrices, "tour_passenger");
  }

  /**
   * Calcula el precio de habitación individual
   * @param {boolean} isSingle - Si es habitación individual
   * @param {Array} tourPrices - Lista de precios del tour
   * @returns {number} Precio de habitación individual
   */
  static calculateSinglePrice(isSingle, tourPrices) {
    if (!isSingle) {
      return 0;
    }

    return this.getPriceBySubType(tourPrices, "tour_single");
  }

  /**
   * Calcula el precio del modelo de vehículo
   * @param {number} modelId - ID del modelo
   * @param {Array} tourPrices - Lista de precios del tour
   * @param {Array} models - Lista de modelos disponibles
   * @returns {number} Precio del modelo
   */
  static calculateModelPrice(modelId, tourPrices, models) {
    if (!modelId || !models || !Array.isArray(models)) {
      return 0;
    }

    const selectedModel = head(filter(propEq("id", modelId), models));
    if (!selectedModel || selectedModel.model_name === "Own Motorcycle") {
      return 0;
    }

    const selectedUpPrice = head(
      filter(
        propEq("price_item_sub_type", `tour_${selectedModel.upgrade_type}`),
        tourPrices
      )
    );
    return selectedUpPrice ? selectedUpPrice.price : 0;
  }

  /**
   * Calcula el precio total para una opción específica de motocicleta (first choice o second choice)
   * @param {Object} tourData - Datos del tour
   * @param {Object} options - Opciones seleccionadas
   * @param {Array} tourPrices - Lista de precios del tour
   * @param {Array} gpsPrices - Lista de precios de GPS
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {Array} models - Lista de modelos disponibles
   * @param {string} choiceType - 'first' o 'second' choice
   * @param {Object} discounts - Descuentos aplicados
   * @returns {number} Precio total para la opción de motocicleta
   */
  static calculateMotorcycleChoicePrice(tourData, options, tourPrices, gpsPrices, insurancePrices, models, choiceType, discounts = {}) {
    const effectiveDays = this.calculateEffectiveDays(
      tourData.booking.tour.effective_start_date,
      tourData.booking.tour.effective_end_date
    );

    // Obtener el modelo correspondiente según el tipo de elección
    let modelId = options.modelId;
    let modelName = options.modelName;
    
    if (choiceType === 'second') {
      // Para second choice, usar el segundo modelo si está disponible
      modelId = options.modelId2 || options.modelId;
      modelName = options.modelName2 || options.modelName;
    }

    const basePrice = this.calculateBasePrice(tourData, modelName, tourPrices);
    const gpsPrice = this.calculateGpsPrice(options.gps, gpsPrices, effectiveDays, modelName);
    const vipInsurancePrice = this.calculateVipInsurancePrice(options.insurance, insurancePrices, effectiveDays, modelName);
    const passengerPrice = this.calculatePassengerPrice(tourData, tourPrices);
    const singlePrice = this.calculateSinglePrice(options.single, tourPrices);
    const modelPrice = this.calculateModelPrice(modelId, tourPrices, models);
    
    // INCLUIR SUPLEMENTOS PENDIENTES EN EL CÁLCULO DEL TOTAL
    const pendingSupplements = options.pendingSupplements || 0;
    
    // CORRECCIÓN: Seguir la lógica exacta del componente original
    // Tanto first choice como second choice SÍ incluyen rewards y discount
    // SEGUIR LA LÓGICA EXACTA DE CustomizedTables (calcTotalTour)
    // En calcTotalTour: totalCost = cancelInsuranceValue + (vipPrice + gpsPrice + tourTotal + singlePrice + passengerPrice + modelPrice - discount - rewards)
    // IMPORTANTE: Usar tourTotal en lugar de tourTotal1/tourTotal2 para mantener consistencia con CustomizedTables
    const subtotal = basePrice + gpsPrice + vipInsurancePrice + passengerPrice + singlePrice + modelPrice + pendingSupplements;

    // Aplicar descuentos como en calcTotalTour
    return subtotal - (discounts.discount || 0) - (discounts.rewards || 0);
  }

  /**
   * Calcula el precio del seguro de cancelación
   * @param {boolean} hasCancelInsurance - Si tiene seguro de cancelación
   * @param {Object} tourData - Datos del tour
   * @param {Array} tourPrices - Lista de precios del tour
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {Object} options - Opciones seleccionadas
   * @param {number} effectiveDays - Días efectivos
   * @param {Array} models - Lista de modelos disponibles
   * @returns {number} Precio del seguro de cancelación
   */
  static calculateCancelInsurancePrice(hasCancelInsurance, tourData, tourPrices, insurancePrices, options, effectiveDays, models) {
    if (!hasCancelInsurance) {
      return 0;
    }

    // Calcular base para el seguro de cancelación
    const basePrice = this.calculateBasePrice(tourData, options.modelName, tourPrices);
    const gpsPrice = this.calculateGpsPrice(options.gps, tourPrices, effectiveDays, options.modelName);
    const passengerPrice = this.calculatePassengerPrice(tourData, tourPrices);
    const singlePrice = this.calculateSinglePrice(options.single, tourPrices);
    const modelPrice = this.calculateModelPrice(options.modelId, tourPrices, models);

    const totalBase = basePrice + gpsPrice + passengerPrice + singlePrice + modelPrice;
    
    // Obtener porcentaje del seguro de cancelación
    const cancelInsurancePercentage = this.getPercentageById(insurancePrices, 3); // ID del seguro de cancelación
    
    return (totalBase * cancelInsurancePercentage) / 100;
  }

  /**
   * Calcula el precio total del tour de manera centralizada
   * @param {Object} tourData - Datos del tour
   * @param {Object} options - Opciones seleccionadas
   * @param {Array} tourPrices - Lista de precios del tour
   * @param {Array} gpsPrices - Lista de precios de GPS
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {Array} models - Lista de modelos disponibles
   * @param {Object} discounts - Descuentos aplicados
   * @returns {Object} Desglose completo de precios
   */
  static calculateTotalPricing(tourData, options, tourPrices, gpsPrices, insurancePrices, models, discounts = {}) {
    const effectiveDays = this.calculateEffectiveDays(
      tourData.booking.tour.effective_start_date,
      tourData.booking.tour.effective_end_date
    );

    const basePrice = this.calculateBasePrice(tourData, options.modelName, tourPrices);
    const gpsPrice = this.calculateGpsPrice(options.gps, gpsPrices, effectiveDays, options.modelName);
    const vipInsurancePrice = this.calculateVipInsurancePrice(options.insurance, insurancePrices, effectiveDays, options.modelName);
    const passengerPrice = this.calculatePassengerPrice(tourData, tourPrices);
    const singlePrice = this.calculateSinglePrice(options.single, tourPrices);
    const modelPrice = this.calculateModelPrice(options.modelId, tourPrices, models);
    const cancelInsurancePrice = this.calculateCancelInsurancePrice(
      options.cancelInsurance, 
      tourData, 
      tourPrices, 
      insurancePrices, 
      options, 
      effectiveDays,
      models
    );

    const subtotal = basePrice + gpsPrice + vipInsurancePrice + passengerPrice + singlePrice + modelPrice;
    const total = subtotal + cancelInsurancePrice - (discounts.rewards || 0) - (discounts.discount || 0);

    return {
      total,
      subtotal,
      effectiveDays,
      breakdown: {
        basePrice,
        gpsPrice,
        vipInsurancePrice,
        passengerPrice,
        singlePrice,
        modelPrice,
        cancelInsurancePrice,
        effectiveDays
      }
    };
  }

  /**
   * Calcula el valor a cargar como First choice of motorcycle menos los valores ya pagados
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Resultado del cálculo de precios
   * @param {boolean} hasCancelInsurance - Si tiene seguro de cancelación
   * @returns {number} Valor a cargar
   */
  static calculateValueToCharge(tourData, pricing, hasCancelInsurance) {
    // Obtener valores ya pagados (charges con status true)
    let totalPayed = 0;
    if (tourData.payments && tourData.payments.length > 0) {
      const charges = prop("charges", head(tourData.payments));
      const totalRefunded = sum(map(prop("refunded_value"), filter(propEq("status", true), filter(propEq("to_refund", true), charges)))) || 0;
      totalPayed = sum(map(prop("value"), filter(propEq("status", true), charges))) - totalRefunded;
    }
    
    // Obtener el precio de la primera opción de motocicleta
    const firstChoicePrice = pricing.total;
    
    // Value to be charged = First choice of motorcycle - valores ya pagados
    return firstChoicePrice - totalPayed;
  }

  /**
   * Valida que los cálculos de precios sean consistentes
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Resultado del cálculo de precios
   * @returns {Object} Resultado de la validación
   */
  static validatePricing(tourData, pricing) {
    const errors = [];
    const warnings = [];
    
    // Validar que los días efectivos sean correctos
    const expectedDays = this.calculateEffectiveDays(
      tourData.booking.tour.effective_start_date,
      tourData.booking.tour.effective_end_date
    );
    
    if (pricing.breakdown.effectiveDays !== expectedDays) {
      errors.push(`Días efectivos incorrectos: esperado ${expectedDays}, calculado ${pricing.breakdown.effectiveDays}`);
    }
    
    // Validar que el GPS se calcule correctamente
    if (pricing.breakdown.gpsPrice > 0) {
      const expectedGpsPrice = (pricing.breakdown.gpsPrice / pricing.breakdown.effectiveDays) * expectedDays;
      if (Math.abs(pricing.breakdown.gpsPrice - expectedGpsPrice) > 0.01) {
        warnings.push(`Precio de GPS puede estar mal calculado: ${pricing.breakdown.gpsPrice} vs ${expectedGpsPrice}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Valida la consistencia entre los precios de motocicletas en diferentes componentes
   * @param {Object} tourData - Datos del tour
   * @param {Object} options - Opciones seleccionadas
   * @param {Array} tourPrices - Lista de precios del tour
   * @param {Array} gpsPrices - Lista de precios de GPS
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {Array} models - Lista de modelos disponibles
   * @param {Object} discounts - Descuentos aplicados
   * @returns {Object} Resultado de la validación de consistencia
   */
  static validateMotorcyclePriceConsistency(tourData, options, tourPrices, gpsPrices, insurancePrices, models, discounts = {}) {
    const errors = [];
    const warnings = [];

    // Calcular precios usando el método estándar
    const standardPricing = this.calculateTotalPricing(tourData, options, tourPrices, gpsPrices, insurancePrices, models, discounts);
    
    // Calcular precios de motocicletas específicos
    const firstChoicePrice = this.calculateMotorcycleChoicePrice(tourData, options, tourPrices, gpsPrices, insurancePrices, models, 'first', discounts);
    const secondChoicePrice = this.calculateMotorcycleChoicePrice(tourData, options, tourPrices, gpsPrices, insurancePrices, models, 'second', discounts);

    // Validar que los precios sean consistentes
    const standardTotal = standardPricing.total;
    const firstChoiceTotal = firstChoicePrice;
    const secondChoiceTotal = secondChoicePrice;

    // Comparar precios
    if (Math.abs(standardTotal - firstChoiceTotal) > 0.01) {
      errors.push(`Discrepancia en precio de primera opción: Estándar €${standardTotal.toFixed(2)} vs Primera opción €${firstChoiceTotal.toFixed(2)}`);
    }

    if (Math.abs(standardTotal - secondChoiceTotal) > 0.01) {
      errors.push(`Discrepancia en precio de segunda opción: Estándar €${standardTotal.toFixed(2)} vs Segunda opción €${secondChoiceTotal.toFixed(2)}`);
    }

    if (Math.abs(firstChoiceTotal - secondChoiceTotal) > 0.01) {
      warnings.push(`Diferencia entre opciones de motocicleta: Primera €${firstChoiceTotal.toFixed(2)} vs Segunda €${secondChoiceTotal.toFixed(2)}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      prices: {
        standard: standardTotal,
        firstChoice: firstChoiceTotal,
        secondChoice: secondChoiceTotal
      }
    };
  }

  /**
   * Obtiene el precio por subtipo
   * @param {Array} prices - Lista de precios
   * @param {string} subType - Subtipo de precio
   * @returns {number} Precio
   */
  static getPriceBySubType(prices, subType) {
    const price = head(filter(propEq("price_item_sub_type", subType), prices));
    return price ? price.price : 0;
  }

  /**
   * Obtiene el precio por tipo
   * @param {Array} prices - Lista de precios
   * @param {string} type - Tipo de precio
   * @returns {number} Precio
   */
  static getPriceByType(prices, type) {
    const price = head(filter(propEq("price_item_type", type), prices));
    return price ? price.price : 0;
  }

  /**
   * Obtiene el precio por ID
   * @param {Array} prices - Lista de precios
   * @param {number} id - ID del precio
   * @returns {number} Precio
   */
  static getPriceById(prices, id) {
    const price = head(filter(propEq("price_item_id", id), prices));
    return price ? price.price : 0;
  }

  /**
   * Obtiene el porcentaje por ID
   * @param {Array} prices - Lista de precios
   * @param {number} id - ID del precio
   * @returns {number} Porcentaje
   */
  static getPercentageById(prices, id) {
    const price = head(filter(propEq("price_item_id", id), prices));
    return price ? price.percentage : 0;
  }

  /**
   * Verifica si un vehículo está disponible para las fechas del tour
   * @param {Object} vehicle - Vehículo a verificar
   * @param {string} tourStartDate - Fecha de inicio del tour
   * @param {string} tourEndDate - Fecha de fin del tour
   * @returns {boolean} Si está disponible
   */
  static isVehicleAvailable(vehicle, tourStartDate, tourEndDate) {
    if (!vehicle || !vehicle.rentals) {
      return true;
    }

    const tourStart = moment(tourStartDate);
    const tourEnd = moment(tourEndDate);

    return !vehicle.rentals.some(rental => {
      const rentalStart = moment(rental.start_date);
      const rentalEnd = moment(rental.end_date);
      
      return (tourStart.isBefore(rentalEnd) && tourEnd.isAfter(rentalStart));
    });
  }

  /**
   * Obtiene el nombre del modelo por ID
   * @param {Array} models - Lista de modelos
   * @param {number} modelId - ID del modelo
   * @returns {string} Nombre del modelo
   */
  static getModelName(models, modelId) {
    if (!models || !Array.isArray(models)) {
      return "";
    }
    
    const selectedModel = head(filter(propEq("id", modelId), models));
    return selectedModel ? selectedModel.model_name : "";
  }

  /**
   * Calcula el desglose detallado de precios para mostrar en la interfaz
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Resultado del cálculo de precios
   * @returns {Array} Array de componentes de precio
   */
  static getPricingBreakdown(tourData, pricing) {
    const breakdown = [];

    // Precio base
    if (pricing.breakdown.basePrice > 0) {
      breakdown.push({
        label: "Tour Base Price",
        value: pricing.breakdown.basePrice,
        type: "base",
        days: null
      });
    }

    // Precio del pasajero
    if (pricing.breakdown.passengerPrice > 0) {
      breakdown.push({
        label: "Passenger Price",
        value: pricing.breakdown.passengerPrice,
        type: "passenger",
        days: null
      });
    }

    // GPS
    if (pricing.breakdown.gpsPrice > 0) {
      breakdown.push({
        label: "GPS Device",
        value: pricing.breakdown.gpsPrice / pricing.breakdown.effectiveDays,
        type: "gps",
        days: pricing.breakdown.effectiveDays,
        total: pricing.breakdown.gpsPrice
      });
    }

    // Seguro VIP
    if (pricing.breakdown.vipInsurancePrice > 0) {
      breakdown.push({
        label: "VIP Insurance",
        value: pricing.breakdown.vipInsurancePrice / pricing.breakdown.effectiveDays,
        type: "insurance",
        days: pricing.breakdown.effectiveDays,
        total: pricing.breakdown.vipInsurancePrice
      });
    }

    // Habitación individual
    if (pricing.breakdown.singlePrice > 0) {
      breakdown.push({
        label: "Single Room",
        value: pricing.breakdown.singlePrice,
        type: "single",
        days: null
      });
    }

    // Modelo de vehículo
    if (pricing.breakdown.modelPrice > 0) {
      breakdown.push({
        label: "Vehicle Model",
        value: pricing.breakdown.modelPrice,
        type: "model",
        days: null
      });
    }

    // Seguro de cancelación
    if (pricing.breakdown.cancelInsurancePrice > 0) {
      breakdown.push({
        label: "Cancel Insurance",
        value: pricing.breakdown.cancelInsurancePrice,
        type: "cancel_insurance",
        days: null
      });
    }

    return breakdown;
  }
}

export default TourCalculationService; 


// WEBPACK FOOTER //
// ./src/services/TourCalculationService.js